/**
 * Login `/login` — legacy email/password sign-in plus Google sign-in.
 * New accounts are created through Google only; there is deliberately no
 * password-registration path in the portal.
 * On success react-router sends the user through `/welcome`, preserving the
 * exact installed-game URL when login began inside a standalone game. The
 * same-origin referrer is captured before Google's redirect and carried in
 * sessionStorage until the provider returns.
 *
 * Google sign-in is a REDIRECT: the button navigates away and the provider
 * returns to THIS page, so the page also owns the completion step — without it
 * a failed round-trip would come back looking like an ordinary blank login
 * form. Success needs no handling here; it arrives as an auth change.
 */
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useServices } from '../services/context.js';
import { Mascot } from '../components/Mascot.js';
import {
  captureGameAuthReturn,
  clearGameAuthReturn,
  welcomePathFor,
} from '../auth/gameReturn.js';

export function Login() {
  const services = useServices();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = useMemo(
    () => captureGameAuthReturn(document.referrer, window.location.origin, sessionStorage, new URLSearchParams(location.search).get('returnTo')),
    [location.search],
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Once signed in (non-anon), leave through the claim step. Returning players
  // then resume their installed game; ordinary portal logins still land home.
  useEffect(() => {
    if (!loading && user && !user.isAnonymous) {
      clearGameAuthReturn(sessionStorage);
      navigate(welcomePathFor(returnTo), { replace: true });
    }
  }, [loading, user, navigate, returnTo]);

  // Land the Google redirect. A no-op on a normal visit; on a failed provider
  // round-trip this is the ONLY place the reason surfaces.
  useEffect(() => {
    services.auth.completeRedirectSignIn().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Google sign-in failed.');
    });
  }, [services]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await services.auth.signInEmail(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed.');
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setError('');
    setBusy(true);
    try {
      // Navigates away when it works — `busy` stays set so the form can't be
      // driven while the browser is leaving. Only a failure to even START the
      // redirect lands back here, and that one must release the form.
      await services.auth.signInGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed.');
      setBusy(false);
    }
  }

  return (
    <div className="container auth">
      <Mascot context="login" size={250} />
      <div className="panel" style={{ marginTop: 14 }}>
        <h1 className="panel__title">Sign in</h1>
        <form onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label className="field__label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <div className="error-text" data-testid="auth-error">{error}</div>}
          <button
            className="btn btn--primary"
            type="submit"
            disabled={busy}
            style={{ width: '100%' }}
            data-testid="login-submit"
          >
            Sign in
          </button>
        </form>

        <div className="divider">or</div>
        <button className="btn" onClick={() => void google()} disabled={busy} style={{ width: '100%' }}>
          Continue with Google
        </button>

        <p
          className="muted"
          style={{ marginTop: 16, fontSize: 13 }}
          data-testid="google-account-notice"
        >
          New here? Continue with Google to create your account. Password registration is no
          longer available.
        </p>
      </div>
    </div>
  );
}
